import { useState } from 'react'
import { ChevronLeft, ChevronRight, X, Image } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { API_BASE_URL } from '@/lib/constants'

interface Photo {
  id: number
  url: string
  caption?: string | null
}

interface Props {
  photos: Photo[]
  facilityName: string
}

/** MinIO 파일명을 다운로드 가능한 URL로 변환 */
function toPhotoUrl(url: string) {
  if (url.startsWith('http')) return url
  return `${API_BASE_URL}/files/${url}`
}

export default function PhotoGallery({ photos, facilityName }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  if (!photos || photos.length === 0) {
    return (
      <div className="h-48 w-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
        <div className="text-center">
          <Image size={32} className="mx-auto text-gray-300 mb-1" />
          <p className="text-xs text-gray-400">사진이 없습니다</p>
        </div>
      </div>
    )
  }

  const prev = () => setCurrentIndex((i) => (i === 0 ? photos.length - 1 : i - 1))
  const next = () => setCurrentIndex((i) => (i === photos.length - 1 ? 0 : i + 1))

  return (
    <>
      {/* 메인 슬라이더 */}
      <div className="relative h-56 w-full bg-gray-100 overflow-hidden group cursor-pointer" onClick={() => setLightboxOpen(true)}>
        <AnimatePresence mode="wait">
          <motion.img
            key={photos[currentIndex]!.id}
            src={toPhotoUrl(photos[currentIndex]!.url)}
            alt={photos[currentIndex]!.caption ?? `${facilityName} 사진 ${currentIndex + 1}`}
            className="h-full w-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          />
        </AnimatePresence>

        {/* 캡션 */}
        {photos[currentIndex]!.caption && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8">
            <p className="text-xs text-white">{photos[currentIndex]!.caption}</p>
          </div>
        )}

        {/* 좌우 화살표 */}
        {photos.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); prev() }}
              className="absolute left-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); next() }}
              className="absolute right-2 top-1/2 -translate-y-1/2 flex h-8 w-8 items-center justify-center rounded-full bg-black/30 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {/* 카운터 */}
        {photos.length > 1 && (
          <div className="absolute top-3 right-3 rounded-full bg-black/40 px-2.5 py-1 text-[11px] font-semibold text-white">
            {currentIndex + 1} / {photos.length}
          </div>
        )}

        {/* 도트 인디케이터 */}
        {photos.length > 1 && photos.length <= 10 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {photos.map((_, i) => (
              <button
                key={i}
                onClick={(e) => { e.stopPropagation(); setCurrentIndex(i) }}
                className={`h-1.5 rounded-full transition-all ${i === currentIndex ? 'w-4 bg-white' : 'w-1.5 bg-white/50'}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* 라이트박스 */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightboxOpen(false)}
          >
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
            >
              <X size={24} />
            </button>

            <img
              src={toPhotoUrl(photos[currentIndex]!.url)}
              alt={photos[currentIndex]!.caption ?? facilityName}
              loading="lazy"
              className="max-h-[85vh] max-w-[90vw] object-contain"
              onClick={(e) => e.stopPropagation()}
            />

            {photos.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prev() }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); next() }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
                >
                  <ChevronRight size={24} />
                </button>
              </>
            )}

            {photos[currentIndex]!.caption && (
              <p className="absolute bottom-6 text-sm text-white/80">{photos[currentIndex]!.caption}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
